/** Bounded identity-scoped UI reports and frozen unacknowledged admission input. */
import type { RunAgentInput } from "@ag-ui/core";
import { authCacheScope, subscribeAuthDataIdentity } from "./cacheScope";
import { apiBaseUrl, pb } from "./pocketbase";
import { parseRenderResult, stableDisplayJSON, type DisplayCall, type RenderResult } from "./assistant-display";
import type { ChatComponent } from "./assistant-components";
import { componentChoice, emptySelectionState, parseSelectionState, proposalChoice, type SelectionChoice, type SelectionProposal, type SelectionState } from "./assistant-selection";

interface Report { recording: string; conversation: string; result: RenderResult }
interface Attempt { recording: string; conversation: string; input: RunAgentInput }
interface SelectionEntry {
  recording: string; conversation: string; component_id: string;
  accepted: SelectionChoice; accepted_revision: number; draft: SelectionProposal | null; conflict: boolean;
}
interface Outbox { reports: Report[]; attempts: Attempt[]; selections: SelectionEntry[] }
let epoch = 0;
const listeners = new Set<() => void>();
const changed = () => { epoch++; listeners.forEach(listener => listener()); };
export const chatOutboxRevision = () => epoch;
export function subscribeChatOutbox(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
const prefix = "neoma.ai.display-outbox/1:";
const key = () => prefix + JSON.stringify([apiBaseUrl, authCacheScope(), pb.authStore.record?.access_revision ?? 0]);
let previousKey = key();
subscribeAuthDataIdentity(() => {
  // Rotation within a principal/access revision does not trigger this callback.
  try { if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(previousKey); } catch { /* Protected state is inaccessible. */ }
  previousKey = key();
  try { if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(previousKey); } catch { /* Reads fail visibly until storage returns. */ }
  changed();
});
function read(): Outbox {
  if (authCacheScope() === "anonymous") return { reports: [], attempts: [], selections: [] };
  const raw = sessionStorage.getItem(key());
  if (!raw) return { reports: [], attempts: [], selections: [] };
  if (raw.length > 1024*1024) throw new TypeError("Assistant outbox too large; reconcile saved state");
  const value = JSON.parse(raw) as Outbox;
  if (!Array.isArray(value.reports) || value.reports.length > 20 || !Array.isArray(value.attempts) || value.attempts.length > 20) throw new TypeError("Invalid assistant outbox");
  for (const report of value.reports) parseRenderResult(report.result);
  value.selections ??= []; // Existing reports/uncertain v2 admissions are retained.
  if (!Array.isArray(value.selections) || value.selections.length > 400) throw new TypeError("Invalid selection drafts");
  for (const entry of value.selections) {
    if (typeof entry.recording !== "string" || typeof entry.conversation !== "string" || typeof entry.conflict !== "boolean" ||
      !Number.isSafeInteger(entry.accepted_revision) || entry.accepted_revision < 0 || entry.accepted_revision > 1e9 + 1 ||
      Object.keys(entry).some(k => !["recording", "conversation", "component_id", "accepted", "accepted_revision", "draft", "conflict"].includes(k))) throw new TypeError("Invalid selection draft");
    parseSelectionState({ schema: "neoma.selection-state/1", selections: [{ component_id: entry.component_id, expected_revision: 0, ...entry.accepted }] });
    if (entry.draft) {
      parseSelectionState({ schema: "neoma.selection-state/1", selections: [entry.draft] });
      if (entry.draft.component_id !== entry.component_id) throw new TypeError("Selection association mismatch");
    }
  }
  return value;
}
function write(value: Outbox) {
  if (authCacheScope() === "anonymous") throw new TypeError("Assistant identity required");
  if (value.reports.length > 20 || value.attempts.length > 20) throw new TypeError("Assistant outbox full; reconcile earlier conversations");
  if (value.selections.length > 400 || new Set(value.selections.map(s => JSON.stringify([s.recording, s.conversation]))).size > 20 ||
    value.selections.some(s => value.selections.filter(v => v.recording === s.recording && v.conversation === s.conversation).length > 20)) throw new TypeError("Selection draft storage full; review earlier drafts");
  const raw = JSON.stringify(value);
  if (raw.length > 1024*1024) throw new TypeError("Assistant outbox too large; reconcile saved state");
  if (sessionStorage.getItem(key()) !== raw) { sessionStorage.setItem(key(), raw); changed(); }
}
export function pendingRenderResults(recording: string, conversation: string): RenderResult[] {
  return read().reports.filter(r => r.recording === recording && r.conversation === conversation).map(r => r.result);
}
export function recordDisplayResult(recording: string, conversation: string, call: DisplayCall, error?: RenderResult["error"]): RenderResult {
  if (call.component.conversation_id !== conversation) throw new TypeError("Display result association mismatch");
  const box = read(), prior = box.reports.find(r => r.result.toolCallId === call.id);
  if (prior) {
    if (prior.recording !== recording || prior.conversation !== conversation) throw new TypeError("Display result association mismatch");
    return prior.result;
  }
  if (box.reports.some(r => r.recording === recording && r.conversation === conversation)) throw new TypeError("Earlier display result awaits admission");
  const result: RenderResult = { id: crypto.randomUUID().replaceAll("-", "").slice(0, 15), role: "tool", toolCallId: call.id,
    content: `${call.name === "show_hypnogram" ? "Hypnogram" : "Sleep-stage breakdown"} ${error ? "could not be displayed." : "displayed."}`,
    ...(error ? { error } : {}) };
  box.reports.push({ recording, conversation, result }); write(box);
  return result;
}
export function freezeChatAdmission(recording: string, conversation: string, input: RunAgentInput): RunAgentInput {
  const box = read(), prior = box.attempts.find(a => a.input.runId === input.runId);
  if (prior) {
    if (prior.recording !== recording || prior.conversation !== conversation || stableDisplayJSON(prior.input) !== stableDisplayJSON(input)) throw new TypeError("Run ID input conflict");
    return prior.input;
  }
  if (box.attempts.some(a => a.recording === recording && a.conversation === conversation)) throw new TypeError("Reconcile the original admission before another send");
  box.attempts.push({ recording, conversation, input }); write(box);
  return input;
}
export function frozenChatAdmission(recording: string, conversation: string, runId: string): RunAgentInput | undefined {
  return read().attempts.find(a => a.recording === recording && a.conversation === conversation && a.input.runId === runId)?.input;
}
export function unacknowledgedChatAdmission(recording: string, conversation: string): string | undefined {
  return read().attempts.find(a => a.recording === recording && a.conversation === conversation)?.input.runId;
}
export function chatAdmissionKnown(recording: string, conversation: string, runId: string, accepted: { component_id: string; revision: number; representation?: string; selection?: { time_seconds: number } | null }[] = []) {
  const box = read(), attempt = box.attempts.find(a => a.input.runId === runId && a.recording === recording && a.conversation === conversation);
  if (!attempt) return;
  if ((attempt.input.forwardedProps as { schema?: string })?.schema === "neoma.run/3") {
    for (const proposal of parseSelectionState(attempt.input.state).selections) {
      const saved = accepted.find(s => s.component_id === proposal.component_id);
      if (!saved || saved.revision !== proposal.expected_revision + 1) throw new TypeError("Missing accepted selection revision");
      const actual = "representation" in proposal ? { representation: saved.representation } : { time_seconds: saved.selection?.time_seconds ?? null };
      if (stableDisplayJSON(actual) !== stableDisplayJSON(proposalChoice(proposal))) throw new TypeError("Accepted selection differs from frozen input");
      let entry = box.selections.find(s => s.recording === recording && s.conversation === conversation && s.component_id === proposal.component_id);
      if (!entry) { entry = { recording, conversation, component_id: proposal.component_id, accepted: proposalChoice(proposal), accepted_revision: saved.revision, draft: null, conflict: false }; box.selections.push(entry); }
      if (saved.revision >= entry.accepted_revision) { entry.accepted = proposalChoice(proposal); entry.accepted_revision = saved.revision; }
      if (entry.draft && stableDisplayJSON(entry.draft) === stableDisplayJSON(proposal)) entry.draft = null;
      else if (entry.draft?.expected_revision === proposal.expected_revision) entry.draft = { ...entry.draft, expected_revision: saved.revision };
      entry.conflict = Boolean(entry.draft && entry.accepted_revision > entry.draft.expected_revision);
    }
  }
  const results = attempt.input.messages.filter(m => m.role === "tool");
  box.reports = box.reports.filter(report => !results.some(result => stableDisplayJSON(result) === stableDisplayJSON(report.result)));
  box.attempts = box.attempts.filter(a => a !== attempt); write(box);
}
export function chatAdmissionRejected(recording: string, conversation: string, runId: string) {
  const box = read();
  box.attempts = box.attempts.filter(a => !(a.input.runId === runId && a.recording === recording && a.conversation === conversation));
  write(box); // Keep every report; the question was not admitted.
}
export function reconcileRenderResults(recording: string, conversation: string, accepted: RenderResult[]): boolean {
  const box = read(); let conflict = false;
  box.reports = box.reports.filter(report => {
    if (report.recording !== recording || report.conversation !== conversation) return true;
    const saved = accepted.find(result => result.toolCallId === report.result.toolCallId);
    if (!saved) return true;
    if (stableDisplayJSON(saved) !== stableDisplayJSON(report.result)) conflict = true;
    return false;
  });
  write(box); return conflict;
}
export function clearChatOutbox() { try { sessionStorage.removeItem(key()); } finally { changed(); } }

function matching(entry: SelectionEntry, recording: string, component: ChatComponent) {
  return entry.recording === recording && entry.conversation === component.conversation_id && entry.component_id === component.id;
}
export function selectionView(recording: string, component: ChatComponent) {
  const entry = read().selections.find(s => matching(s, recording, component));
  const newer = entry && entry.accepted_revision > component.state.revision;
  const accepted = newer ? entry.accepted : componentChoice(component), revision = newer ? entry.accepted_revision : component.state.revision;
  return { choice: entry?.draft ? proposalChoice(entry.draft) : accepted, accepted, revision, dirty: Boolean(entry?.draft), conflict: Boolean(entry?.draft && (entry.conflict || revision > entry.draft.expected_revision)) };
}
export function chooseLocalSelection(recording: string, component: ChatComponent, choice: SelectionChoice) {
  const box = read(); let entry = box.selections.find(s => matching(s, recording, component));
  const view = selectionView(recording, component);
  const proposal = { component_id: component.id, expected_revision: entry?.draft?.expected_revision ?? view.revision, ...choice };
  parseSelectionState({ schema: "neoma.selection-state/1", selections: [proposal] });
  if (component.payload?.type === "hypnogram") {
    if (!("time_seconds" in choice) || (choice.time_seconds !== null && (component.payload.status !== "available" || !component.payload.timeline || choice.time_seconds > component.payload.timeline.duration_seconds))) throw new TypeError("Invalid local time selection");
  } else if (component.payload?.type !== "sleep_stage_breakdown" || !("representation" in choice)) throw new TypeError("Invalid local chart selection");
  if (!entry) { entry = { recording, conversation: component.conversation_id, component_id: component.id, accepted: view.accepted, accepted_revision: view.revision, draft: null, conflict: false }; box.selections.push(entry); }
  entry.draft = proposal;
  entry.conflict ||= view.conflict;
  write(box);
}
export function observeAcceptedSelections(recording: string, components: ChatComponent[]) {
  const box = read();
  box.selections = box.selections.filter(entry => {
    const component = components.find(c => matching(entry, recording, c));
    if (!component || component.state.revision < entry.accepted_revision) return true;
    entry.accepted = componentChoice(component); entry.accepted_revision = component.state.revision;
    if (!entry.draft) return false;
    entry.conflict = entry.accepted_revision > entry.draft.expected_revision;
    return true;
  });
  write(box);
}
export function reviewSelectionDraft(recording: string, component: ChatComponent, keep: boolean) {
  const box = read(), entry = box.selections.find(s => matching(s, recording, component));
  if (!entry?.draft) return;
  const view = selectionView(recording, component);
  entry.accepted = view.accepted; entry.accepted_revision = view.revision;
  entry.draft = keep ? { ...entry.draft, expected_revision: view.revision } : null;
  entry.conflict = false; write(box);
}
export function pendingSelectionState(recording: string, conversation: string): SelectionState {
  const entries = read().selections.filter(s => s.recording === recording && s.conversation === conversation && s.draft);
  if (entries.some(s => s.conflict)) throw new TypeError("Review selections changed by another client before sending");
  return parseSelectionState({ ...emptySelectionState(), selections: entries.map(s => s.draft!) });
}
export function selectionDraftStatus(recording: string, conversation: string) {
  try {
    const entries = read().selections.filter(s => s.recording === recording && s.conversation === conversation && s.draft);
    return { count: entries.length, conflict: entries.some(s => s.conflict), error: "" };
  } catch { return { count: 0, conflict: false, error: "Local selection storage is unavailable. Sending is disabled; saved server selections remain available." }; }
}
